import { Play, ArrowUpRight } from "lucide-react";
import { BRAND, EPISODES, MEDIA } from "@/data/content";

const platforms = [
  {
    key: "spotify",
    label: "Spotify",
    href: BRAND.podcastLinks.spotify,
    color: "#1DB954",
  },
  {
    key: "youtube",
    label: "YouTube",
    href: BRAND.podcastLinks.youtube,
    color: "#FF0033",
  },
  {
    key: "youtube-music",
    label: "YouTube Music",
    href: BRAND.podcastLinks.youtubeMusic,
    color: "#FF4E45",
  },
];

export default function Podcast() {
  return (
    <section
      id="podcast"
      data-testid="podcast-section"
      className="relative py-28 md:py-36 px-6 sm:px-10 lg:px-16 bg-[#070707]"
    >
      <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left — Cover + platforms */}
        <div className="lg:col-span-5">
          <div className="text-xs font-sans tracking-[0.22em] uppercase text-zinc-500 overline-line">
            The Podcast
          </div>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-light leading-[1.05] text-white">
            Tapping Into Life <br />
            <span className="font-accent text-brand-gold text-4xl sm:text-5xl">the podcast</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-lg leading-relaxed max-w-md">
            Tender, brave conversations on healing, intuition, and the
            soft revolution of living from the inside out. New episodes weekly.
          </p>

          <div className="relative mt-10 group">
            <div className="absolute -inset-2 bg-gradient-to-tr from-brand-amber/40 via-brand-violet/20 to-brand-azure/10 rounded-3xl blur-2xl opacity-70" />
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-black">
              <img
                src={MEDIA.podcast}
                alt="Tapping Into Life Podcast"
                className="w-full aspect-square object-contain p-4"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <button
                data-testid="podcast-hero-play"
                onClick={() => window.open(BRAND.podcastLinks.spotify, "_blank")}
                className="absolute bottom-6 left-6 right-6 flex items-center justify-between bg-white/5 backdrop-blur-md border border-white/15 px-5 py-4 rounded-2xl hover:bg-white/10 transition"
              >
                <div className="flex items-center gap-4 text-left">
                  <span className="h-12 w-12 rounded-full bg-brand-amber flex items-center justify-center">
                    <Play size={18} fill="black" className="text-black ml-0.5" />
                  </span>
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Latest episode</div>
                    <div className="text-white font-serif text-lg leading-tight">
                      {EPISODES[0].title}
                    </div>
                  </div>
                </div>
                <ArrowUpRight className="text-zinc-300" />
              </button>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3" data-testid="podcast-platforms">
            {platforms.map((p) => (
              <a
                key={p.key}
                data-testid={`podcast-platform-${p.key}`}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 hover:border-white/40 hover:bg-white/5 transition text-sm text-zinc-200"
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: p.color }}
                />
                Listen on {p.label}
              </a>
            ))}
          </div>
        </div>

        {/* Right — Episode list */}
        <div className="lg:col-span-7">
          <div className="text-xs tracking-[0.22em] uppercase text-zinc-500 mb-6">
            Featured Episodes
          </div>
          <div className="divide-y divide-brand-border border-y border-brand-border">
            {EPISODES.map((ep, i) => (
              <a
                key={ep.id}
                data-testid={`episode-${ep.id}`}
                href={BRAND.podcastLinks.spotify}
                target="_blank"
                rel="noopener noreferrer"
                className="group grid grid-cols-12 gap-4 py-7 items-start hover:bg-white/[0.02] transition px-2 -mx-2"
              >
                <div className="col-span-2 sm:col-span-1 font-serif text-3xl text-brand-gold/60 group-hover:text-brand-amber transition">
                  {ep.number}
                </div>
                <div className="col-span-10 sm:col-span-9">
                  <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight group-hover:text-brand-amber transition">
                    {ep.title}
                  </h3>
                  <p className="mt-2 text-zinc-400 max-w-xl leading-relaxed">
                    {ep.summary}
                  </p>
                  <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500 tracking-wider uppercase">
                    <span>{ep.date}</span>
                    <span className="h-1 w-1 rounded-full bg-zinc-700" />
                    <span>{ep.duration}</span>
                  </div>
                </div>
                <div className="hidden sm:flex col-span-2 justify-end items-center">
                  <span className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center group-hover:border-brand-amber group-hover:bg-brand-amber transition">
                    <Play size={14} className="ml-0.5 text-white group-hover:text-black transition" fill="currentColor" />
                  </span>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-4">
            <a
              data-testid="podcast-view-all"
              href={BRAND.podcastLinks.spotify}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white font-semibold group"
            >
              <span className="border-b border-brand-amber pb-0.5">All episodes</span>
              <ArrowUpRight size={18} className="text-brand-amber group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
